import { useAuth } from 'react-oidc-context';
import { Capacitor } from '@capacitor/core';
import { ximiOidcClient, ximiUserManager } from './oidcConfig';
import { openXimiAuthBrowser } from './openXimiAuthBrowser';

/** Optional tenant + login pushed to the SSO page (pre-fill) via OIDC query params. */
export interface XimiLoginHints {
    tenantCode: string;
    loginHint: string;
}

/**
 * Single entry point for Ximi SSO auth in the app. Wraps react-oidc-context's
 * useAuth() to expose a small, stable surface (login / logout / token / state).
 *
 * Web → full-page redirect (react-oidc-context). Native → open the /authorize URL in an
 * in-app webview and intercept the redirect (openXimiAuthBrowser), then signinCallback.
 */
export function useXimiSSOAuth() {
    const auth = useAuth();

    const login = async (hints?: XimiLoginHints) => {
        // 'tenant-code' & 'login_hint' are the params the AuthProvider reads to pre-fill
        // its login page. Ignored by the dev mock — only visible in QA.
        const extraQueryParams = hints
            ? { 'tenant-code': hints.tenantCode, login_hint: hints.loginHint }
            : undefined;

        if (Capacitor.isNativePlatform()) {
            // request_type 'si:r' so signinCallback routes to the redirect-callback handler
            // (else: "invalid request_type in state").
            const request = await ximiOidcClient.createSigninRequest({
                request_type: 'si:r',
                extraQueryParams
            });
            const callbackUrl = await openXimiAuthBrowser(request.url);
            if (callbackUrl) await ximiUserManager.signinCallback(callbackUrl);
            return;
        }
        await auth.signinRedirect({ extraQueryParams });
    };

    return {
        login,
        logout: () => auth.signoutRedirect(),
        token: auth.user?.access_token ?? null,
        isAuthenticated: auth.isAuthenticated,
        isLoading: auth.isLoading,
        user: auth.user ?? null,
        error: auth.error ?? null
    };
}
